'use client';

import React, { useMemo, useRef, useState, useCallback } from 'react';
import Image from 'next/image';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { SlidersHorizontal, ChevronDown } from 'lucide-react';

import { imageUrl } from '@/lib/medusa';
import {
    getProductPriceInfo,
    getDisplayAmount,
} from '@/lib/data/products';
import PriceTag from '@/components/PriceTag';
import { useTranslation } from '@/lib/i18n/useTranslation';
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from '@/components/ui/select';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const DEFAULT_IMAGE = '/images/default-product-image.jpg';
const GOLD = '#D48937';

const PRICE_BUCKETS = [
    { id: 'all', test: () => true },
    { id: 'under100', test: (p) => p < 100 },
    { id: '100to300', test: (p) => p >= 100 && p <= 300 },
    { id: 'over300', test: (p) => p > 300 },
];

const SORT_OPTIONS = ['newest', 'featured', 'priceAsc', 'priceDesc', 'nameAsc'];

function getCreatedTime(product) {
    const raw = product?.created_at;
    if (!raw) return 0;
    const t = new Date(raw).getTime();
    return Number.isNaN(t) ? 0 : t;
}

function getStockState(product) {
    const v = product?.variants?.[0];
    if (!v || !v.manage_inventory) {
        return { isSold: false, hasIndicator: false };
    }
    const qty = v.inventory_quantity ?? 0;
    return { isSold: qty <= 0, hasIndicator: true };
}

const ShopView = ({ products = [], promotions = [], onProductClick }) => {
    const { t, locale } = useTranslation();
    const containerRef = useRef(null);

    const [category, setCategory] = useState('all');
    const [priceRange, setPriceRange] = useState('all');
    const [sort, setSort] = useState('newest');
    const [filtersOpen, setFiltersOpen] = useState(false);

    const shopDict = t('home.shop');
    const typeLabels = shopDict.typeLabels || {};

    const availableTypes = useMemo(() => {
        const map = new Map();
        for (const p of products) {
            const v = p?.type?.value;
            if (v && !map.has(v)) map.set(v, true);
        }
        return Array.from(map.keys());
    }, [products]);

    const filtered = useMemo(() => {
        const bucket = PRICE_BUCKETS.find((b) => b.id === priceRange) || PRICE_BUCKETS[0];
        // Filter and sort on the displayed price (after sale + promotion) so the
        // shop is consistent with what the customer actually sees on each card.
        const amountOf = (p) => getDisplayAmount(p, promotions);

        let list = products.filter((p) => {
            if (category !== 'all' && p?.type?.value !== category) return false;
            const amount = amountOf(p);
            if (priceRange !== 'all') {
                if (amount == null) return false;
                if (!bucket.test(Number(amount))) return false;
            }
            return true;
        });

        if (sort === 'newest') {
            list = [...list].sort((a, b) => getCreatedTime(b) - getCreatedTime(a));
        } else if (sort === 'priceAsc') {
            list = [...list].sort(
                (a, b) => (amountOf(a) ?? Infinity) - (amountOf(b) ?? Infinity),
            );
        } else if (sort === 'priceDesc') {
            list = [...list].sort(
                (a, b) => (amountOf(b) ?? -Infinity) - (amountOf(a) ?? -Infinity),
            );
        } else if (sort === 'nameAsc') {
            list = [...list].sort((a, b) =>
                (a.title || '').localeCompare(b.title || '', locale === 'de' ? 'de' : 'en'),
            );
        }

        return list;
    }, [products, promotions, category, priceRange, sort, locale]);

    const isFiltered =
        category !== 'all' || priceRange !== 'all' || sort !== 'newest';

    const clearFilters = useCallback(() => {
        setCategory('all');
        setPriceRange('all');
        setSort('newest');
    }, []);

    useGSAP(
        () => {
            const items = gsap.utils.toArray('.shop-grid-item');
            if (items.length === 0) return;

            const mm = gsap.matchMedia();
            mm.add(
                {
                    motionOK: '(prefers-reduced-motion: no-preference)',
                    motionReduced: '(prefers-reduced-motion: reduce)',
                },
                (context) => {
                    const { motionOK } = context.conditions;
                    if (!motionOK) {
                        gsap.set(items, { autoAlpha: 1, y: 0 });
                        return;
                    }

                    gsap.set(items, { autoAlpha: 0, y: 24 });

                    ScrollTrigger.batch(items, {
                        start: 'top 92%',
                        once: true,
                        onEnter: (batch) =>
                            gsap.to(batch, {
                                autoAlpha: 1,
                                y: 0,
                                duration: 0.7,
                                ease: 'power2.out',
                                stagger: 0.06,
                                overwrite: true,
                            }),
                    });

                    ScrollTrigger.refresh();
                },
            );
        },
        { scope: containerRef, dependencies: [filtered], revertOnUpdate: true },
    );

    const labelCls = 'text-[10px] uppercase tracking-[0.3em] text-neutral-500';

    const minimalTriggerCls =
        'h-auto w-full sm:w-auto sm:min-w-[9rem] gap-3 rounded-none border-0 border-b border-neutral-300 bg-transparent px-0 py-2.5 text-[11px] uppercase tracking-[0.3em] text-neutral-800 hover:border-neutral-900 focus:outline-none focus:ring-0 focus:ring-offset-0 transition-colors data-[state=open]:border-neutral-900 [&>svg]:opacity-60';

    const dropdownContentCls =
        'min-w-[10rem] rounded-none border border-neutral-200 bg-white text-neutral-800 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.28)] font-pp-neue';

    const dropdownItemCls =
        'rounded-none py-2.5 pl-8 pr-6 text-[10px] uppercase tracking-[0.3em] text-neutral-500 cursor-pointer transition-colors focus:bg-neutral-100 focus:text-neutral-900 data-[highlighted]:bg-neutral-100 data-[highlighted]:text-neutral-900 data-[state=checked]:text-neutral-900';

    return (
        <div ref={containerRef} className="font-pp-neue px-4 md:px-12 pb-16">
            <div className="mx-auto max-w-[1500px]">
                <div className="border-y border-neutral-200 py-4 md:py-5 lg:py-6 mb-8 md:mb-10 lg:mb-12">
                    {/* Compact bar for phones/tablets: result count + filter toggle */}
                    <div className="flex items-center justify-between gap-4 lg:hidden">
                        <span className="text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                            {shopDict.results(filtered.length)}
                        </span>
                        <button
                            type="button"
                            onClick={() => setFiltersOpen((open) => !open)}
                            aria-expanded={filtersOpen}
                            aria-controls="shop-filters"
                            className="inline-flex items-center gap-2 py-1.5 text-[11px] uppercase tracking-[0.3em] text-neutral-800 transition-colors hover:text-neutral-900"
                        >
                            <SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={1.5} />
                            <span>{shopDict.filters}</span>
                            {isFiltered && (
                                <span
                                    aria-hidden
                                    className="h-1.5 w-1.5 rounded-full"
                                    style={{ backgroundColor: GOLD }}
                                />
                            )}
                            <ChevronDown
                                className={`h-4 w-4 opacity-60 transition-transform duration-300 ${filtersOpen ? 'rotate-180' : ''}`}
                                strokeWidth={1.5}
                            />
                        </button>
                    </div>

                    {/* Controls: collapsible below lg, inline row at lg+ */}
                    <div
                        id="shop-filters"
                        className={`flex-col gap-6 pt-5 lg:flex lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:pt-0 ${filtersOpen ? 'flex' : 'hidden'}`}
                    >
                        <div className="flex flex-col gap-3">
                            <span className={`${labelCls} hidden lg:block`}>{shopDict.filters}</span>
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                                <CategoryPill
                                    active={category === 'all'}
                                    onClick={() => setCategory('all')}
                                >
                                    {shopDict.allCategories}
                                </CategoryPill>
                                {availableTypes.map((typeValue) => (
                                    <CategoryPill
                                        key={typeValue}
                                        active={category === typeValue}
                                        onClick={() => setCategory(typeValue)}
                                    >
                                        {typeLabels[typeValue] || typeValue}
                                    </CategoryPill>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:gap-6">
                            <div className="flex flex-col gap-2">
                                <span className={labelCls}>{shopDict.price.label}</span>
                                <Select value={priceRange} onValueChange={setPriceRange}>
                                    <SelectTrigger className={minimalTriggerCls}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className={dropdownContentCls} position="popper" sideOffset={6}>
                                        {PRICE_BUCKETS.map((b) => (
                                            <SelectItem key={b.id} value={b.id} className={dropdownItemCls}>
                                                {shopDict.price[b.id]}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex flex-col gap-2">
                                <span className={labelCls}>{shopDict.sort.label}</span>
                                <Select value={sort} onValueChange={setSort}>
                                    <SelectTrigger className={minimalTriggerCls}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className={dropdownContentCls} position="popper" sideOffset={6}>
                                        {SORT_OPTIONS.map((opt) => (
                                            <SelectItem key={opt} value={opt} className={dropdownItemCls}>
                                                {shopDict.sort[opt]}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {isFiltered && (
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="self-start text-[11px] uppercase tracking-[0.3em] text-neutral-800 underline-offset-4 hover:underline hover:text-neutral-900 transition-colors lg:hidden"
                            >
                                {shopDict.clear}
                            </button>
                        )}
                    </div>

                    {/* Result count + clear for desktop */}
                    <div className="mt-5 hidden flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.3em] lg:flex">
                        <span className="text-neutral-500">
                            {shopDict.results(filtered.length)}
                        </span>
                        {isFiltered && (
                            <>
                                <span aria-hidden className="h-px w-6 bg-neutral-300" />
                                <button
                                    type="button"
                                    onClick={clearFilters}
                                    className="text-neutral-800 underline-offset-4 hover:underline hover:text-neutral-900 transition-colors"
                                >
                                    {shopDict.clear}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {filtered.length === 0 ? (
                    <div className="flex flex-col items-center gap-5 py-24 text-center">
                        <span aria-hidden className="h-px w-12" style={{ background: GOLD }} />
                        <p className="text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                            {shopDict.empty}
                        </p>
                        {isFiltered && (
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="mt-2 text-[11px] uppercase tracking-[0.3em] text-neutral-800 underline-offset-4 hover:underline"
                            >
                                {shopDict.clear}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-10 lg:grid-cols-4">
                        {filtered.map((product) => (
                            <ShopCard
                                key={product.id}
                                product={product}
                                promotions={promotions}
                                locale={locale}
                                soldLabel={shopDict.sold}
                                onClick={() => onProductClick?.(product)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

function CategoryPill({ active, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`
                relative cursor-pointer text-[12px] uppercase tracking-[0.3em] py-1.5
                ${active ? 'text-neutral-900' : 'text-neutral-500'} hover:text-neutral-900 transition-colors
                after:absolute after:bottom-0 after:left-0 after:w-full after:h-px
                after:bg-neutral-900 after:transition-all after:duration-300
                ${active ? 'after:scale-x-100' : 'after:scale-x-0 hover:after:scale-x-100'}
            `}
        >
            {children}
        </button>
    );
}

function ShopCard({ product, promotions = [], locale, soldLabel, onClick }) {
    const thumb = product.thumbnail || product.images?.[0]?.url;
    const src = thumb ? imageUrl(thumb) : DEFAULT_IMAGE;
    const priceInfo = getProductPriceInfo(product, promotions);
    const { isSold } = getStockState(product);

    return (
        <button
            type="button"
            onClick={onClick}
            className="shop-grid-item group text-left flex flex-col"
        >
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-neutral-100">
                <Image
                    src={src}
                    alt={product.title}
                    fill
                    sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
                {isSold && (
                    <div className="absolute top-3 left-3 bg-white/90 px-2 py-1 text-[10px] uppercase tracking-[0.3em] text-neutral-800">
                        {soldLabel}
                    </div>
                )}
            </div>
            <div className="mt-3 flex flex-col gap-1">
                <h3 className="text-base tracking-wide text-neutral-900 group-hover:opacity-70 transition-opacity">
                    {product.title}
                </h3>
                {(product.subtitle || product.description) && (
                    <p className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 line-clamp-1">
                        {product.subtitle || product.description}
                    </p>
                )}
                {priceInfo?.amount != null && (
                    <p className="mt-1 text-sm tracking-wide text-neutral-800">
                        <PriceTag
                            info={priceInfo}
                            locale={locale}
                            showBadge
                            saleClassName="text-neutral-900"
                        />
                    </p>
                )}
            </div>
        </button>
    );
}

export default ShopView;
